import { useEffect, useRef, type ReactNode } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, Check, Search, Settings, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface EntryPickerOverlayProps {
  open: boolean;
  /** Header title — the parent category's name once drilled in. */
  title: string;
  /** Back arrow. Pops one level, or closes the picker at the root. */
  onBack: () => void;
  query: string;
  onQueryChange: (q: string) => void;
  searchOpen: boolean;
  onSearchOpenChange: (open: boolean) => void;
  /**
   * Opened in a new tab, deliberately: navigating away from an open picker
   * would discard the half-entered transaction underneath it.
   */
  settingsHref?: string;
  settingsLabel?: string;
  children: ReactNode;
}

/**
 * Full-screen picker shell — the chrome shared by the category and account
 * pickers so the two read as one system: back arrow, title, search, settings.
 *
 * It sits above the entry screen rather than replacing it, so dismissing it
 * always returns to a form with every other field still filled in.
 */
export function EntryPickerOverlay({
  open,
  title,
  onBack,
  query,
  onQueryChange,
  searchOpen,
  onSearchOpenChange,
  settingsHref,
  settingsLabel,
  children,
}: EntryPickerOverlayProps) {
  const shouldReduceMotion = useReducedMotion();
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || !searchOpen) return;
    const t = setTimeout(() => searchRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open, searchOpen]);

  // Escape pops this layer only — it must not fall through and close the
  // whole entry screen, which would lose the entry.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onBack();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onBack]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          initial={shouldReduceMotion ? { opacity: 0 } : { x: "100%" }}
          animate={shouldReduceMotion ? { opacity: 1 } : { x: 0 }}
          exit={shouldReduceMotion ? { opacity: 0 } : { x: "100%" }}
          transition={
            shouldReduceMotion
              ? { duration: 0.1 }
              : { type: "spring", damping: 32, stiffness: 380 }
          }
          className="fixed inset-0 z-[60] flex h-[100dvh] flex-col bg-[var(--cp-canvas)]"
        >
          <header className="flex h-14 shrink-0 items-center gap-1 border-b border-[var(--cp-line)] bg-[var(--cp-surface)] px-1">
            <button
              type="button"
              onClick={onBack}
              aria-label="Back"
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--cp-ink-2)] transition-colors hover:bg-[var(--cp-surface-2)]"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>

            {searchOpen ? (
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Search…"
                aria-label={`Search ${title.toLowerCase()}`}
                className="min-h-10 min-w-0 flex-1 bg-transparent text-base text-[var(--cp-ink)] placeholder:text-[var(--cp-ink-3)] focus:outline-none"
              />
            ) : (
              <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-[var(--cp-ink)]">
                {title}
              </h2>
            )}

            <button
              type="button"
              onClick={() => {
                if (searchOpen) onQueryChange("");
                onSearchOpenChange(!searchOpen);
              }}
              aria-label={searchOpen ? "Close search" : "Search"}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--cp-ink-2)] transition-colors hover:bg-[var(--cp-surface-2)]"
            >
              {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>

            {settingsHref && (
              <a
                href={settingsHref}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={settingsLabel ?? "Manage"}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[var(--cp-ink-2)] transition-colors hover:bg-[var(--cp-surface-2)]"
              >
                <Settings className="h-5 w-5" />
              </a>
            )}
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto pb-[max(0.5rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Small uppercase band heading — `MOST FREQUENT`, `ALL CATEGORIES`, … */
export function PickerSectionHeading({ children }: { children: ReactNode }) {
  return (
    <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-[var(--cp-ink-3)]">
      {children}
    </p>
  );
}

/**
 * One list row: filled colour disc + label, 48px tall, hairline divider.
 * No chevrons and no chips — the reference's row anatomy, and the reason a
 * 40-category list scans in one pass where a chip grid did not.
 */
export function PickerRow({
  color,
  label,
  sublabel,
  selected,
  onClick,
  glyph,
}: {
  color: string;
  label: string;
  sublabel?: string;
  selected?: boolean;
  onClick: () => void;
  /** Defaults to the label's first character. */
  glyph?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      // Explicit, so the accessible name is the category/account itself and
      // never picks up the colour disc's initial as a leading letter.
      aria-label={sublabel ? `${label} (${sublabel})` : label}
      className={cn(
        "flex min-h-12 w-full items-center gap-3 border-b border-[var(--cp-line-2)] px-4 py-2 text-left transition-colors",
        selected ? "bg-[var(--cp-accent-wash)]" : "hover:bg-[var(--cp-surface-2)]",
      )}
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
        style={{ backgroundColor: color }}
        aria-hidden
      >
        {glyph ?? label.trim().charAt(0).toUpperCase()}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] text-[var(--cp-ink)]">{label}</span>
        {sublabel && (
          <span className="block truncate text-xs text-[var(--cp-ink-3)]">{sublabel}</span>
        )}
      </span>
      {selected && <Check className="h-4 w-4 shrink-0 text-[var(--cp-accent-ink)]" />}
    </button>
  );
}
